import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { CabinetSummary, fetchCabinetSummary } from "services/cabinet.service";
import { setError, setLogout, setSession } from "features/auth/authSlice";

/**
 * The cabinet summary of ONE person in ONE company at a time: the bell, the
 * «Užduotys» menu badge, the menu hints, the tab badges and the «Užduotys» page
 * all read this. `userId` and `companyId` say whose numbers `data` holds; a
 * different person or company never sees it, and it is dropped when the person
 * logs out or another one signs in. A failed request keeps the last `data` and
 * only sets `failed`, so a hiccup never turns the bell to zero.
 */
export interface CabinetSummaryState {
  userId: string | null;
  companyId: string | null;
  data: CabinetSummary | null;
  /** The last request failed; `data` (if any) is the older answer. */
  failed: boolean;
  /** When the latest request started (ms), so the page can skip a request made a moment ago. */
  requestedAt: number | null;
  /** Counts requests; only the answer to the latest one is kept. */
  latestRequest: number;
}

const initialState: CabinetSummaryState = {
  userId: null,
  companyId: null,
  data: null,
  failed: false,
  requestedAt: null,
  latestRequest: 0,
};

// Forgets everything, and retires every request still in flight by moving the
// counter on (starting from zero again could match an old request's number).
const forgetEverything = (state: CabinetSummaryState): CabinetSummaryState => ({
  ...initialState,
  latestRequest: state.latestRequest + 1,
});

const cabinetSummarySlice = createSlice({
  name: "cabinet-summary",
  initialState,
  reducers: {
    requested(
      state,
      action: PayloadAction<{ userId: string; companyId: string; requestId: number; at: number }>
    ) {
      const { userId, companyId, requestId, at } = action.payload;
      state.latestRequest = requestId;
      state.requestedAt = at;
      if (state.userId !== userId || state.companyId !== companyId) {
        // Another person's or company's numbers never show under this one.
        state.userId = userId;
        state.companyId = companyId;
        state.data = null;
        state.failed = false;
      }
    },
    loaded(state, action: PayloadAction<{ requestId: number; data: CabinetSummary }>) {
      if (action.payload.requestId !== state.latestRequest) return;
      state.data = action.payload.data;
      state.failed = false;
    },
    failed(state, action: PayloadAction<{ requestId: number }>) {
      if (action.payload.requestId !== state.latestRequest) return;
      state.failed = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setLogout, forgetEverything)
      .addCase(setError, forgetEverything)
      .addCase(setSession, (state, action) =>
        action.payload.session?.id === state.userId ? state : forgetEverything(state)
      );
  },
});

export default cabinetSummarySlice.reducer;

/** A store without these slices (a small test store) reads as "nothing loaded". */
type StateWithSummary = {
  cabinetSummaryReducer?: CabinetSummaryState;
  authReducer?: { session?: { id?: string } | null };
};

/** A summary asked for this recently is not asked for again by a caller that allows it. */
export const JUST_ASKED_MS = 10 * 1000;

/**
 * Asks the summary of the signed-in person for this company. With `maxAgeMs`, a
 * request that started that recently (answered or still on its way) is enough and
 * nothing new is sent.
 */
export const fetchCabinetSummaryThunk =
  (
    companyId: string,
    { maxAgeMs = 0 }: { maxAgeMs?: number } = {}
  ): ThunkAction<Promise<void>, StateWithSummary, undefined, AnyAction> =>
  async (dispatch, getState) => {
    const userId = getState().authReducer?.session?.id;
    const current = getState().cabinetSummaryReducer;
    if (!userId || !current) return;

    const isRecent =
      current.userId === userId &&
      current.companyId === companyId &&
      !current.failed &&
      current.requestedAt !== null &&
      Date.now() - current.requestedAt < maxAgeMs;
    if (isRecent) return;

    const requestId = current.latestRequest + 1;
    dispatch(cabinetSummarySlice.actions.requested({ userId, companyId, requestId, at: Date.now() }));
    try {
      const data = await fetchCabinetSummary(companyId);
      dispatch(cabinetSummarySlice.actions.loaded({ requestId, data }));
    } catch (e) {
      dispatch(cabinetSummarySlice.actions.failed({ requestId }));
    }
  };

// The slice, when it holds the summary of the signed-in person for this company.
function sliceOf(state: StateWithSummary, companyId?: string) {
  const slice = state.cabinetSummaryReducer;
  const userId = state.authReducer?.session?.id;
  return slice && userId && slice.userId === userId && slice.companyId === companyId ? slice : null;
}

/** The summary of this company, or null while it is not loaded (or is somebody else's). */
export const selectCabinetSummary = (state: StateWithSummary, companyId?: string) =>
  sliceOf(state, companyId)?.data ?? null;

export const selectCabinetSummaryFailed = (state: StateWithSummary, companyId?: string) =>
  sliceOf(state, companyId)?.failed ?? false;
